<template>
  <el-popover
    v-model="popoverVisible"
    placement="left"
    width="480"
    trigger="click"
    popper-class="assign-dialog-popover"
    @show="initPopover"
  >
    <div v-rect-loading="loading" class="assign-dialog-content">
      <Assign
        labelI18nKey="admin.talent.label.recruiterAccess"
        :selectIds.sync="currentOwnerInfo.account_ids"
        :disabled="!canEdit"
        :optionList="accountList"
      ></Assign>
      <Assign
        labelI18nKey="admin.talent.label.roleAccess"
        :selectIds.sync="currentOwnerInfo.role_ids"
        :disabled="!canEdit"
        :optionList="roleList"
      ></Assign>
      <div v-if="showInterviewerCanView" class="assign-switch-row">
        <span class="row-label">{{ $t(interviewerLabelI18nKey) }}</span>
        <div class="row-content">
          <el-switch
            v-model="currentInterviewerCanView"
            class="assign-switch"
            active-color="#0096cc"
            :active-text="$t('common.yes')"
            :inactive-text="$t('common.no')"
            :width="switchWidth"
            :disabled="!canEdit"
          ></el-switch>
        </div>
      </div>
    </div>
    <div v-if="canEdit" class="popover-footer">
      <el-button class="footer-btn cancel-btn" @click="handleClose">{{ $t('common.btn.cancel') }}</el-button>
      <el-button class="footer-btn save-btn" @click="handleSave">{{ $t('common.btn.confirm') }}</el-button>
    </div>
    <div slot="reference" class="trigger-label">
      <div v-if="showBadge" class="assign-count-badge">
        <span class="count">{{ accountCount }}</span>
        <span class="label">{{ getCountLabel(accountCount, 'admin.talent.label.recruiter') }}</span>
      </div>
      <div v-if="showBadge" class="assign-count-badge">
        <span class="count">{{ roleCount }}</span>
        <span class="label">{{ getCountLabel(roleCount, 'admin.talent.label.role') }}</span>
      </div>
      <div v-if="!showBadge">
        <i class="iconfont iconAdd"></i>
      </div>
    </div>
  </el-popover>
</template>

<script>
import Assign from '../components/assign.vue'

export default {
  components: {
    Assign
  },
  props: {
    canEdit: {
      type: Boolean,
      default: true
    },
    ownerInfo: {
      type: Object,
      default: () => ({ account_ids: [], role_ids: [] })
    },
    accountList: {
      type: Array,
      default: () => []
    },
    roleList: {
      type: Array,
      default: () => []
    },
    showInterviewerCanView: {
      type: Boolean,
      default: false
    },
    interviewerCanView: {
      type: Boolean,
      default: false
    },
    interviewerLabelI18nKey: {
      type: String,
      default: 'admin.application.candidate.label.interviewerCanView'
    }
  },
  data () {
    return {
      popoverVisible: false,
      loading: false,
      currentOwnerInfo: {
        account_ids: [],
        role_ids: []
      },
      currentInterviewerCanView: false
    }
  },
  computed: {
    accountCount () {
      return this.ownerInfo?.account_ids?.length || 0
    },
    roleCount () {
      return this.ownerInfo?.role_ids?.length || 0
    },
    showBadge () {
      return this.accountCount > 0 || this.roleCount > 0
    },
    switchWidth () {
      return this.$i18n.locale === 'en' ? 55 : 50
    }
  },
  methods: {
    initPopover () {
      this.currentOwnerInfo = {
        account_ids: [...(this.ownerInfo?.account_ids || [])],
        role_ids: [...(this.ownerInfo?.role_ids || [])]
      }
      this.currentInterviewerCanView = this.interviewerCanView
    },
    handleSave () {
      if (this.loading) {
        return
      }
      this.loading = true
      this.$emit('update:interviewerCanView', this.currentInterviewerCanView)
      this.$emit('save', this.currentOwnerInfo, () => {
        this.loading = false
        this.popoverVisible = false
      })
    },
    handleClose () {
      this.popoverVisible = false
    },
    getCountLabel (count, i18nKey) {
      const label = this.$t(i18nKey)
      if (this.$i18n.locale === 'en' && count > 1) {
        return `${label}s`
      }
      return label
    }
  }
}
</script>

<style lang="scss" scoped>
.trigger-label {
  color: #0096cc;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 1.4;

  .assign-count-badge {
    display: inline-flex;
    align-items: center;

    .count {
      margin-right: 4px;
    }
  }
}
</style>

<style lang="scss">
.assign-dialog-popover {
  padding: 20px 20px 18px !important;

  .assign-dialog-content {
    padding: 4px 0;
  }

  .assign-switch-row {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    .row-label {
      width: 120px;
      font-size: 14px;
      color: #606266;
      line-height: 28px;
      flex-shrink: 0;
      text-align: right;
      margin-right: 20px;
    }

    .row-content {
      flex: 1;
      display: flex;
      align-items: flex-start;
    }

    .assign-switch {
      .el-switch__label {
        display: none;
        position: absolute;
        height: 100%;
        color: #ffffff;
        z-index: 1;
        margin: 0;

        &.is-active {
          display: inline-block;
        }
      }

      .el-switch__label--left {
        left: 25px;
      }

      .el-switch__core {
        width: 55px;
      }

      .el-switch__label--right {
        left: 10px;
      }
    }
  }

  .popover-footer {
    margin-top: 16px;
    padding-top: 12px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    .footer-btn  {
      border-radius: 2px;
      padding: 10px 30px;
    }

    .save-btn {
      background: #0096cc;
      border-color: #0096cc;
      color: #ffffff;

      &:hover {
        background: #008fc3;
        border-color: #008fc3;
      }
    }

    .is-disabled {
      background: #0096cc;
      border-color: #0096cc;

      &:hover {
        background: #0096cc;
        border-color: #0096cc;
      }
    }

    .cancel-btn {
      background: #ffffff;
      color: #606266;
    }
  }
}
</style>
